import { X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { speciesOrFirstWord, matchSpecies, stripSpecies } from "@/lib/species";
import { fmtNum } from "@/lib/format";

const lumberDesc = (d) => { const sp = matchSpecies(d); const ex = stripSpecies(d); return sp && ex ? `${sp} - ${ex}` : (d || '').trim(); };

export function LumberEntry({
  item, idx, origIdx, l, pcsPerCant, wasteInches, displayQty, isExtra, extras, optionNum = 0,
  remainingPieces, totalPiecesCut, readOnly, updateItemStopwatch, setRemoveExtraEntry,
}) {
  const customQtyVal = item.lumber_custom_qty?.[origIdx] || "";
  const mainSpecies = item.lumber_custom_species?.[origIdx] || speciesOrFirstWord(l.description);

  // Real-time inventory adjustment: positive delta = lumber used (deduct), negative = put back
  const autoAdjust = async (deltaCants, species) => {
    if (!deltaCants) return;
    if (localStorage.getItem('lumber_auto_deduct') === 'off') return;
    try {
      const { data } = await axios.post(`${API}/lumber-inventory/auto-adjust`, {
        thickness: parseFloat(l.thickness) || null, width: l.width || null,
        length: parseFloat(l.length) || null, species: species || mainSpecies, amount: -deltaCants,
      });
      if (data && data.adjusted === false) {
        toast.warning("No matching lumber in inventory — nothing was deducted", { id: `no-match-${idx}-${origIdx}` });
      }
    } catch {}
  };

  return (
    <div className={`p-2 border-2 rounded-sm ${remainingPieces === 0 && totalPiecesCut > 0 ? 'border-green-400 bg-green-50/50' : 'border-violet-400 bg-violet-50/50'}`}>
      {optionNum > 0 && <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700 mb-1">Lumber Option {optionNum}</p>}
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold flex-1 min-w-0 break-words">
          <span className={totalPiecesCut > 0 && remainingPieces === 0 ? 'text-green-600' : ''} data-testid={`lumber-display-qty-${idx}-${origIdx}`}>{fmtNum(displayQty)} Cants{isExtra ? ' extra' : ''}</span> - {l.thickness ? `${l.thickness}"` : ''}{l.width && l.width !== 'Random' ? ` x ${l.width}"` : l.width === 'Random' ? ' x Rndm' : ''} x {l.length}&apos;{l.description ? ` ${lumberDesc(l.description)}` : ''}
          {pcsPerCant > 0 && <span className="text-[10px] font-normal text-violet-500 ml-1">({fmtNum(pcsPerCant)} pcs/cant)</span>}
        </span>
      </div>
      {!readOnly && (
      <div className="ml-6 mt-2 space-y-1.5">
        {/* Row 1: Cants Used + Adjust */}
        <div className="flex items-end gap-2 sm:gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Cants Used</p>
            <span
              className="inline-flex w-14 sm:w-20 min-h-[44px] items-center justify-center px-0.5 text-base sm:text-lg text-center border rounded-none font-bold border-violet-300 bg-violet-50 text-zinc-800 break-words"
              data-testid={`lumber-custom-qty-${idx}-${origIdx}`}
            >{customQtyVal ? fmtNum(customQtyVal) : '—'}</span>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Adjust</p>
            <div className="flex items-center gap-0.5">
              <input type="number" min="1" id={`lumber-adj-${idx}-${origIdx}`} placeholder="#"
                onInput={(e) => { if (e.target.value.length > 6) e.target.value = e.target.value.slice(0, 6); }}
                className="w-12 sm:w-16 h-11 text-base text-center border border-zinc-200 bg-white focus:border-zinc-400 focus:outline-none" />
              <button onClick={() => {
                const inp = document.getElementById(`lumber-adj-${idx}-${origIdx}`);
                const v = parseFloat(inp?.value) || 0; if (v <= 0) return;
                const curr = parseFloat(customQtyVal) || 0;
                const upd = [...(item.lumber_custom_qty || item.lumber.map(() => ""))];
                upd[origIdx] = String(curr + v);
                const counts = { ...(item.lumber_adj_counts || {}) };
                const c = counts[origIdx] || { plus: 0, minus: 0 };
                counts[origIdx] = { ...c, plus: (c.plus || 0) + 1 };
                updateItemStopwatch(item.id, { lumber_custom_qty: upd, lumber_adj_counts: counts });
                autoAdjust(v, mainSpecies);
                if (inp) inp.value = "";
              }} className="text-lg font-bold px-2.5 sm:px-3 h-11 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 active:bg-zinc-200"
                data-testid={`lumber-adj-add-${idx}-${origIdx}`}>+</button>
              <button onClick={() => {
                const inp = document.getElementById(`lumber-adj-${idx}-${origIdx}`);
                const v = parseFloat(inp?.value) || 0; if (v <= 0) return;
                const curr = parseFloat(customQtyVal) || 0;
                const applied = Math.min(v, curr);
                const upd = [...(item.lumber_custom_qty || item.lumber.map(() => ""))];
                upd[origIdx] = String(Math.max(0, curr - v));
                const counts = { ...(item.lumber_adj_counts || {}) };
                const c = counts[origIdx] || { plus: 0, minus: 0 };
                counts[origIdx] = { ...c, minus: (c.minus || 0) + 1 };
                updateItemStopwatch(item.id, { lumber_custom_qty: upd, lumber_adj_counts: counts });
                autoAdjust(-applied, mainSpecies);
                if (inp) inp.value = "";
              }} className="text-lg font-bold px-2.5 sm:px-3 h-11 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 active:bg-zinc-200"
                data-testid={`lumber-adj-sub-${idx}-${origIdx}`}>−</button>
            </div>
            {((item.lumber_adj_counts?.[origIdx]?.plus || 0) > 0 || (item.lumber_adj_counts?.[origIdx]?.minus || 0) > 0) && (
              <div className="flex items-center gap-2 mt-0.5 text-[9px] text-zinc-400">
                {(item.lumber_adj_counts?.[origIdx]?.plus || 0) > 0 && <span className="text-green-600">+{item.lumber_adj_counts[origIdx].plus} adds</span>}
                {(item.lumber_adj_counts?.[origIdx]?.minus || 0) > 0 && <span className="text-red-500">−{item.lumber_adj_counts[origIdx].minus} subs</span>}
              </div>
            )}
          </div>
        </div>
        {/* Row 2: Waste + Species */}
        <div className="flex items-end gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Waste</p>
            <span className="text-xs font-semibold text-zinc-800">{wasteInches > 0 ? `${wasteInches}"` : 'None'}</span>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Species</p>
            <span className="text-xs font-semibold text-zinc-800" data-testid={`lumber-species-${idx}-${origIdx}`}>
              {mainSpecies || '—'}
            </span>
          </div>
        </div>
      </div>
      )}
      {readOnly && (
      <div className="flex items-start gap-4 ml-6 mt-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Cants Used</p>
          <span className="text-xs font-bold text-zinc-800">{customQtyVal || '—'}</span>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Waste</p>
          <span className="text-xs font-semibold text-zinc-800">{wasteInches > 0 ? `${wasteInches}"` : 'None'}</span>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Species</p>
          <span className="text-xs font-semibold text-zinc-800">{mainSpecies || '—'}</span>
        </div>
      </div>
      )}
      {/* Extra species entries */}
      {!readOnly && extras.map((ex, ei) => (
        <div key={ei} className="flex items-center gap-3 ml-6 mt-1.5">
          <input
            type="text" defaultValue={ex.qty || ""}
            onChange={(e) => {
              if (e.target.value.length > 6) e.target.value = e.target.value.slice(0, 6);
              const oldQty = parseFloat(ex.qty || "") || 0;
              const newQty = parseFloat(e.target.value) || 0;
              const allExtras = { ...(item.lumber_extra_used || {}) };
              const arr = [...(allExtras[origIdx] || [])];
              arr[ei] = { ...arr[ei], qty: e.target.value };
              allExtras[origIdx] = arr;
              updateItemStopwatch(item.id, { lumber_extra_used: allExtras });
              autoAdjust(newQty - oldQty, ex.species || mainSpecies);
            }}
            placeholder="#" className="w-16 h-6 text-[11px] text-center border rounded-none focus:outline-none font-bold border-violet-300 bg-white focus:border-violet-500"
            data-testid={`lumber-extra-qty-${idx}-${origIdx}-${ei}`}
          />
          <span className="text-[11px] text-zinc-700" data-testid={`lumber-extra-species-${idx}-${origIdx}-${ei}`}>
            {ex.species || mainSpecies || '—'}
          </span>
          <button
            onClick={() => setRemoveExtraEntry({ origIdx, ei })}
            className="text-zinc-400 hover:text-red-500 text-xs"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
